import type { Annotation } from "@/types"
import { Label } from "@coval/ui"

import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"

import { AggregateOverviewString } from "../../components/aggregate/AggregateOverviewString"
import {
  ProgressMetrics,
  type MetricExplanation,
} from "../../components/ProgressMetrics"
import { CategoricalMetricRecord } from "../base/CategoricalMetricRecord"
import { CategoricalHumanFeedbackButton } from "../helpers/CategoricalHumanFeedbackButton"
import { parseResultJson } from "../helpers/parseResultJson"

type BaseTestCaseRecord = Pick<
  TestCaseRecord,
  "input_str" | "expected_behaviors" | "expected_output_json"
>

const SENTIMENT_CATEGORIES = [
  "Rude",
  "Polite",
  "Encouraging",
  "Professional",
] as const

export class SentimentAnalysisMetricRecord extends CategoricalMetricRecord {
  output_type = "string" as const
  overviewSize = "medium" as const
  //no table cell render

  getCategoricalOptions(): string[] {
    return [...SENTIMENT_CATEGORIES]
  }

  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    context?: {
      runRows?: RunRowLike[]
      highlightValue?: number | string | null
      onBarClick?: (filter: { type: "exact"; value: string }) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }

    const validOutputs = metricOutputs.filter((mo) => mo.string_value !== null)

    if (validOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">No metric data available.</div>
      )
    }

    return (
      <AggregateOverviewString
        mos={validOutputs}
        onBarClick={context?.onBarClick}
        activeFilters={context?.activeFilters}
      />
    )
  }

  hasCustomVotingUI = true

  renderHumanFeedbackValueButton(
    annotation: Annotation | null,
    onValueChange: (
      floatValue: number | null,
      stringValue: string | null,
      setValue: string[] | null,
    ) => void,
    onExplanationChange: (explanation: string | null) => void,
    otherUserAnnotations?: Annotation[],
  ): React.ReactNode {
    return (
      <CategoricalHumanFeedbackButton
        annotation={annotation}
        onValueChange={onValueChange}
        onExplanationChange={onExplanationChange}
        otherUserAnnotations={otherUserAnnotations}
        categories={[...SENTIMENT_CATEGORIES]}
      />
    )
  }

  _renderSentimentChart(
    mo: RunMetricOutput,
    dispatchMessageSelect?: (messageIndex: number | null) => void,
  ): React.ReactNode {
    const resultJson = parseResultJson<MetricExplanation>(mo)
    if (!resultJson) {
      return null
    }
    return (
      <div className="w-full h-96">
        <ProgressMetrics
          result_json={resultJson}
          setSelectedMessage={dispatchMessageSelect || (() => {})}
          xLabel="Message Index"
          yLabel="Score"
        />
      </div>
    )
  }

  _renderDeepDive(
    mo: RunMetricOutput,
    _testCase: BaseTestCaseRecord,
    dispatchMessageSelect?: (messageIndex: number | null) => void,
  ): React.ReactNode {
    return this._renderSentimentChart(mo, dispatchMessageSelect)
  }

  // Stack the comment box on top and the plot full-width below, instead of the
  // default side-by-side content/notes columns.
  renderDeepDivePanel(
    notesNode: React.ReactNode,
    metricOutput: RunMetricOutput | undefined,
    onMessageSelect?: (messageIndex: number | null) => void,
  ): React.ReactNode {
    if (!metricOutput) {
      return null
    }
    const chart = this._renderSentimentChart(metricOutput, onMessageSelect)
    return (
      <div className="space-y-6">
        <div className="min-w-0">{notesNode}</div>
        {chart && (
          <div className="min-w-0">
            <Label className="mb-3 block">{this.metric_display_name}</Label>
            {chart}
          </div>
        )}
      </div>
    )
  }
}
